import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { RequestSequenceCache } from './color-group-request-cache.js';

const PRIMARY_FILTER = 'filter.p.m.custom.discovery_primary';
const PRODUCT_LANE_SELECTOR = '[data-predictive-primary-products]';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #searchRequests = new RequestSequenceCache();

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  get #isEditorialDrawer() {
    return this.dataset.searchLayout === 'editorial_drawer' && window.matchMedia('(min-width: 750px)').matches;
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#searchRequests.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#isEditorialDrawer) return;

    requestAnimationFrame(() => {
      this.refs.searchInput.focus({ preventScroll: true });
    });
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter(
        (item) =>
          item instanceof HTMLElement && (!this.#isEditorialDrawer || item.getClientRects().length > 0)
      );

    if (this.#isEditorialDrawer) {
      const quickLinks = Array.from(this.querySelectorAll('.predictive-search-results__quick-link'));
      const firstNonQueryIndex = allItems.findIndex(
        (item) => !item.closest('.predictive-search-results__wrapper-queries')
      );

      allItems.splice(firstNonQueryIndex === -1 ? allItems.length : firstNonQueryIndex, 0, ...quickLinks);
    }

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.#isEditorialDrawer && this.dialog?.refs.dialog.open) {
        event.preventDefault();
        event.stopPropagation();
        this.dialog.closeDialog();
        return;
      }

      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce(() => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const searchModal = this.closest('.search-modal__content--editorial_drawer');
      if (this.#isEditorialDrawer && searchModal instanceof HTMLElement) {
        searchModal.scrollTop = 0;
        return;
      }

      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    if (Theme.colorGroupDiscovery?.enabled !== true) {
      return this.#getNativeSearchResults(searchTerm);
    }

    if (!this.dataset.primaryProductsSectionId) {
      return this.#getNativeSearchResults(searchTerm);
    }

    const productUrl = this.#buildPrimaryProductsUrl(searchTerm);
    const suggestionsUrl = this.#buildNativeSuggestionsUrl(searchTerm);
    const cacheKey = `${productUrl.toString()} ${suggestionsUrl.toString()}`;

    const { predictiveSearchResults } = this.refs;

    if (this.#isEditorialDrawer) {
      predictiveSearchResults.setAttribute('aria-busy', 'true');
    }

    return this.#searchRequests
      .run(cacheKey, async (signal) => {
        const [primaryResponse, suggestionsResponse] = await Promise.all([
          fetch(productUrl.toString(), { credentials: 'same-origin', signal }),
          fetch(suggestionsUrl.toString(), { credentials: 'same-origin', signal }),
        ]);

        if (!suggestionsResponse.ok) {
          throw new Error(`Predictive suggestions request failed with status ${suggestionsResponse.status}`);
        }

        return {
          primaryMarkup: primaryResponse.ok ? await primaryResponse.text() : '',
          suggestionsMarkup: await suggestionsResponse.text(),
        };
      })
      .then((result) => {
        if (result.status === 'stale' || !result.value) return;

        this.#renderSearchLanes(result.value.primaryMarkup, result.value.suggestionsMarkup);
        predictiveSearchResults.removeAttribute('aria-busy');

        this.#resetScrollPositions();
      })
      .catch((error) => {
        predictiveSearchResults.removeAttribute('aria-busy');
        predictiveSearchResults.replaceChildren();
        console.error('Predictive search error:', error);
      });
  }

  #getNativeSearchResults(searchTerm) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    if (this.#isEditorialDrawer) {
      predictiveSearchResults.setAttribute('aria-busy', 'true');
    }

    return sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup || abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        predictiveSearchResults.removeAttribute('aria-busy');
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        predictiveSearchResults.removeAttribute('aria-busy');
        throw error;
      });
  }

  #buildPrimaryProductsUrl(searchTerm) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('type', 'product');
    url.searchParams.set(PRIMARY_FILTER, '1');
    url.searchParams.set('section_id', this.dataset.primaryProductsSectionId);
    return url;
  }

  #buildNativeSuggestionsUrl(searchTerm) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'query,page,collection');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.dataset.sectionId || 'predictive-search');
    return url;
  }

  #renderSearchLanes(primaryMarkup, suggestionsMarkup) {
    const primaryDocument = new DOMParser().parseFromString(primaryMarkup, 'text/html');
    const suggestionsDocument = new DOMParser().parseFromString(suggestionsMarkup, 'text/html');
    const incomingPrimaryLane =
      primaryDocument.querySelector(PRODUCT_LANE_SELECTOR) || primaryDocument.querySelector('#predictive-search-products');
    const renderedResults =
      suggestionsDocument.querySelector('#predictive-search-results') ||
      suggestionsDocument.querySelector('.predictive-search-dropdown');
    const renderedInner = renderedResults?.querySelector('.predictive-search-results__inner');

    if (renderedInner && incomingPrimaryLane) {
      renderedInner.querySelector('.predictive-search-results__no-results')?.remove();
      renderedInner.querySelector('[role="status"][aria-live="polite"]')?.replaceChildren();

      const nativeProducts = renderedInner.querySelector('#predictive-search-products');
      const firstResourceCarousel = renderedInner.querySelector('.predictive-search-results__resource-carousel');

      if (nativeProducts) {
        nativeProducts?.replaceWith(incomingPrimaryLane);
      } else if (firstResourceCarousel) {
        firstResourceCarousel.before(incomingPrimaryLane);
      } else {
        renderedInner.appendChild(incomingPrimaryLane);
      }

      this.#enforceSquareProductMedia(incomingPrimaryLane);
    }

    morph(this.refs.predictiveSearchResults, renderedResults || incomingPrimaryLane || suggestionsDocument.body);
  }

  #enforceSquareProductMedia(root) {
    root.querySelectorAll('.resource-card__media').forEach((media) => {
      if (media instanceof HTMLElement) {
        media.style.setProperty('--resource-card-aspect-ratio', '1 / 1');
      }
    });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#searchRequests.abort();
    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    if (!this.#isEditorialDrawer) {
      const viewedProducts = RecentlyViewed.getProducts();

      if (viewedProducts.length > 0) {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
        if (!recentlyViewedMarkup) return;

        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        if (!recentlyViewedProductsHtml) return;

        for (const child of recentlyViewedProductsHtml.children) {
          if (child instanceof HTMLElement) {
            child.setAttribute('ref', 'recentlyViewedWrapper');
          }
        }

        const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
        if (!collectionElement) return;
        collectionElement.prepend(...recentlyViewedProductsHtml.children);
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
